import {
  LEAF_EXISTING,
  LEAF_NEW,
  REASON_AGREED,
  WARNING_TWO_SAVED_ANSWERS,
  UNCHECKED_MATCHES,
} from "./reviewCodes";
import { isLinked } from "./pathDecision";
import {
  isApprovedAttributeReviewStatus,
  isRejectedAttributeReviewStatus,
} from "./reviewQueueStatus";
import {
  asReviewRow,
  type ReviewGroupKey,
  type RowInput,
} from "./reviewRow.types";

export const GROUP_ORDER: ReviewGroupKey[] = [
  "pick",
  "check",
  "ready",
  "approved",
  "hidden",
  "ignored",
];

export const isSettledGroup = (key: ReviewGroupKey) =>
  key === "approved" || key === "ignored";

export function isDoneRow(input: RowInput, queuedIds: ReadonlySet<string>) {
  const row = asReviewRow(input);
  return (
    isApprovedAttributeReviewStatus(row.decisionStatus) ||
    isRejectedAttributeReviewStatus(row.decisionStatus) ||
    queuedIds.has(String(row.id))
  );
}

export function pickConfidence(input: RowInput): number | null {
  const row = asReviewRow(input);
  if (!row.leaf) return null;
  const scores = (row.agent?.answers ?? [])
    .map((answer) => answer.confidence)
    .filter((p): p is number => typeof p === "number");
  return scores.length ? Math.min(...scores) : null;
}

export function valuesToCheck(input: RowInput): number {
  const row = asReviewRow(input);
  return (row.values ?? []).filter(
    (value) => !value.ignored && UNCHECKED_MATCHES.includes(value.match),
  ).length;
}

export function reviewGroup(
  input: RowInput,
  queuedIds: ReadonlySet<string>,
  sharedLeafIds: ReadonlySet<string> = new Set(),
): ReviewGroupKey {
  const row = asReviewRow(input);
  if (isRejectedAttributeReviewStatus(row.decisionStatus)) return "ignored";
  if (row.hidden) return "hidden";
  if (
    isApprovedAttributeReviewStatus(row.decisionStatus) ||
    queuedIds.has(String(row.id))
  )
    return "approved";
  if (row.agent && !row.agent.agreed) return "pick";
  if (isLinked(row) || row.suggested?.length) return "check";
  if (row.leafRejection || row.warnings?.includes(WARNING_TWO_SAVED_ANSWERS))
    return "check";
  if (!row.leaf || row.leafSource === LEAF_NEW) return "pick";
  return row.reviewReason === REASON_AGREED &&
    row.leafSource === LEAF_EXISTING &&
    valuesToCheck(row) === 0 &&
    !sharedLeafIds.has(String(row.id))
    ? "ready"
    : "check";
}

const labelOf = (row: RowInput) => String(row.sourceAttribute ?? row.id);

export function groupRows<T extends RowInput>(
  rows: T[],
  queuedIds: ReadonlySet<string>,
): { key: ReviewGroupKey; rows: T[] }[] {
  const byGroup = new Map<ReviewGroupKey, T[]>();
  const shared = new Set(sameLeafLabels(rows).keys());
  rows.forEach((row) => {
    const key = reviewGroup(row, queuedIds, shared);
    byGroup.set(key, [...(byGroup.get(key) ?? []), row]);
  });
  return GROUP_ORDER.flatMap((key) => {
    const members = byGroup.get(key);
    if (!members) return [];
    const sorted = [...members].sort(
      (a, b) =>
        (pickConfidence(b) ?? -1) - (pickConfidence(a) ?? -1) ||
        labelOf(a).localeCompare(labelOf(b)),
    );
    return [{ key, rows: sorted }];
  });
}

const normalLabel = (row: RowInput) => labelOf(row).trim().toLowerCase();

export function duplicateLabelIds(rows: RowInput[]): Set<string> {
  const counts = new Map<string, number>();
  rows.forEach((row) =>
    counts.set(normalLabel(row), (counts.get(normalLabel(row)) ?? 0) + 1),
  );
  return new Set(
    rows
      .filter((row) => (counts.get(normalLabel(row)) ?? 0) > 1)
      .map((row) => String(row.id)),
  );
}

export function sameLeafLabels(rows: RowInput[]): Map<string, string> {
  const byPath = new Map<unknown, RowInput[]>();
  rows.forEach((row) => {
    if (row.pathPreview)
      byPath.set(row.pathPreview, [
        ...(byPath.get(row.pathPreview) ?? []),
        row,
      ]);
  });
  const labels = new Map<string, string>();
  rows.forEach((row) => {
    const other = byPath.get(row.pathPreview)?.find((r) => r.id !== row.id);
    if (row.pathPreview && other) labels.set(String(row.id), labelOf(other));
  });
  return labels;
}

export function nextOpenRowId(
  orderedIds: string[],
  currentId: string,
  settledIds: ReadonlySet<string>,
): string | null {
  const at = orderedIds.indexOf(currentId);
  const isOpen = (id: string) => id !== currentId && !settledIds.has(id);
  return (
    orderedIds.slice(at + 1).find(isOpen) ??
    orderedIds.slice(0, Math.max(at, 0)).reverse().find(isOpen) ??
    null
  );
}
